<template>
  <v-row justify="center">
    <v-col md="8" lg="6" xl="4">
      <v-card>
        <v-form @submit.prevent="save" ref="editForm">
          <v-card-title>
            <h2>{{ $t("admin.events.settings.branding.title") }}</h2>
            <v-spacer></v-spacer>
            <v-btn @click="edit()" class="ma-4" color=" primary" v-if="view">
              {{ $t("admin.events.settings.content.edit") }}
            </v-btn>
          </v-card-title>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-image-area</v-icon>
            <v-toolbar-title class="ml-3">{{ $t("admin.events.settings.branding.subheaders.banner") }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-subheader v-if="event.brandingBannerUrl" class="pl-0">{{
              $t("admin.events.settings.branding.banner")
            }}</v-subheader>
            <v-img
              v-if="event.brandingBannerUrl"
              :src="event.brandingBannerUrl || ''"
              contain
              class="mb-5"
              max-height="200px"
            >
              <v-btn
                @click="event.brandingBannerUrl = null"
                class="float-right ma-1"
                color="error"
                x-small
                v-if="event.brandingBannerUrl && editOn"
                fab
              >
                <v-icon dark>mdi-delete-forever</v-icon></v-btn
              >
            </v-img>
            <v-file-input
              :disabled="view"
              v-if="!event.brandingBannerUrl"
              :label="$t('admin.events.settings.branding.banner')"
              accept="image/*"
              v-model="fileBanner"
            ></v-file-input>
            <ColorPicker
              :disabled="view"
              :label="$t('admin.events.settings.branding.bannerBackgroundColor')"
              :selectedColor.sync="event.brandingBannerBackgroundColor"
              :rules="[$v.required()]"
              :valid.sync="valid"
            />
            <v-select
              :disabled="view"
              :items="imageExtendOptions"
              :label="$t('admin.events.settings.branding.bannerExtend')"
              prepend-icon="mdi-crop-free"
              v-model="event.brandingBannerExtend"
            ></v-select>
            <v-select
              :disabled="view"
              :items="imageHorizontalAlignOptions"
              :label="$t('admin.events.settings.branding.bannerHorizontalAlign')"
              prepend-icon="mdi-swap-horizontal"
              v-model="event.brandingBannerHorizontalAlign"
            ></v-select>
            <v-select
              :disabled="view"
              :items="imageVerticalAlignOptions"
              :label="$t('admin.events.settings.branding.bannerVerticalAlign')"
              prepend-icon="mdi-swap-vertical"
              v-model="event.brandingBannerVerticalAlign"
            ></v-select>
          </v-card-text>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-image-auto-adjust</v-icon>
            <v-toolbar-title class="ml-3">{{
              $t("admin.events.settings.branding.subheaders.background")
            }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-subheader v-if="event.brandingContentImageUrl" class="pl-0">{{
              $t("admin.events.settings.branding.content.backgroundImage")
            }}</v-subheader>
            <v-img
              v-if="event.brandingContentImageUrl"
              :src="event.brandingContentImageUrl || ''"
              contain
              class="mb-5"
              max-width="400px"
              max-height="300px"
            >
              <v-btn
                @click="event.brandingContentImageUrl = null"
                class="float-right ma-1"
                color="error"
                x-small
                v-if="event.brandingContentImageUrl && editOn"
                fab
              >
                <v-icon dark>mdi-delete-forever</v-icon></v-btn
              >
            </v-img>
            <v-file-input
              :disabled="view"
              v-if="!event.brandingContentImageUrl"
              :label="$t('admin.events.settings.branding.content.backgroundImage')"
              accept="image/*"
              v-model="fileBackground"
            ></v-file-input>
            <ColorPicker
              :disabled="view"
              :label="$t('admin.events.settings.branding.content.backgroundColor')"
              :selectedColor.sync="event.brandingContentBackgroundColor"
              :rules="[$v.required()]"
              :valid.sync="valid"
            />

            <v-select
              :disabled="view"
              :items="imageExtendOptions"
              :label="$t('admin.events.settings.branding.content.backgroundImageExtend')"
              prepend-icon="mdi-crop-free"
              v-model="event.brandingContentImageExtend"
            ></v-select>
            <v-select
              :disabled="view"
              :items="imageHorizontalAlignOptions"
              :label="$t('admin.events.settings.branding.content.backgroundImageHorizontalAlign')"
              prepend-icon="mdi-swap-horizontal"
              v-model="event.brandingContentImageHorizontalAlign"
            ></v-select>
            <v-select
              :disabled="view"
              :items="imageVerticalAlignOptions"
              :label="$t('admin.events.settings.branding.content.backgroundImageVerticalAlign')"
              prepend-icon="mdi-swap-vertical"
              v-model="event.brandingContentImageVerticalAlign"
            ></v-select>
            <v-slider
              :disabled="view"
              :label="$t('admin.events.settings.branding.content.backgroundImageOpacity')"
              max="100"
              min="0"
              prepend-icon="mdi-image"
              v-model="event.brandingContentImageOpacity"
            >
              <template v-slot:append> {{ event.brandingContentImageOpacity }}% </template>
            </v-slider>
          </v-card-text>
          <v-toolbar color="secondary" dark dense flat class="mt-3">
            <v-icon>mdi-palette</v-icon>
            <v-toolbar-title class="ml-3">{{
              $t("admin.events.settings.branding.subheaders.additional")
            }}</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <ColorPicker
              :disabled="view"
              :label="$t('admin.events.settings.branding.mainColor')"
              :selectedColor.sync="event.brandingMainColor"
              :rules="[$v.required()]"
              :valid.sync="valid"
            />
            <ColorPicker
              :disabled="view"
              :label="$t('admin.events.settings.branding.textColor')"
              :selectedColor.sync="event.brandingTextColor"
              :rules="[$v.required()]"
              :valid.sync="valid"
            />

            <v-select
              :disabled="view"
              :items="themeOptions"
              :label="$t('admin.events.settings.branding.theme')"
              prepend-icon="mdi-brightness-4"
              v-model="event.brandingTheme"
            ></v-select>
            <v-subheader class="pl-0" v-if="event.brandingFaviconUrl">{{
              $t("admin.events.settings.branding.content.favicon")
            }}</v-subheader>
            <v-img
              :src="event.brandingFaviconUrl || ''"
              class="mb-5"
              contain
              max-height="100px"
              max-width="100px"
              v-if="event.brandingFaviconUrl"
            >
              <v-btn
                @click="event.brandingFaviconUrl = null"
                class="float-right ma-1"
                color="error"
                fab
                v-if="event.brandingFaviconUrl && editOn"
                x-small
              >
                <v-icon dark>mdi-delete-forever</v-icon></v-btn
              >
            </v-img>
            <v-file-input
              :disabled="view"
              :label="$t('admin.events.settings.branding.content.favicon')"
              accept="image/*"
              v-if="!event.brandingFaviconUrl"
              v-model="fileFavicon"
            ></v-file-input>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" v-if="!view" :disabled="!valid" type="submit">
              {{ $t("admin.events.settings.branding.save") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from "axios";
import ColorPicker from "../components/color-picker";
export default {
  name: "settings.branding",
  components: {
    ColorPicker,
  },
  data() {
    return {
      event: {},
      valid: true,
      view: true,
      editOn: false,
      menuMainColor: null,
      menuTextColor: null,
      menuBannerBackgroundColor: null,
      menuContentBackgroundColor: null,
      fileLogo: null,
      fileBanner: null,
      fileBackground: null,
      fileFavicon: null,
      themeOptions: [
        {
          text: this.$t("admin.events.settings.branding.themeOptions.light"),
          value: "light",
        },
        {
          text: this.$t("admin.events.settings.branding.themeOptions.dark"),
          value: "dark",
        },
      ],
      imageExtendOptions: [
        {
          text: this.$t("admin.events.settings.branding.extendOptions.contain"),
          value: "Contain",
        },
        {
          text: this.$t("admin.events.settings.branding.extendOptions.cover"),
          value: "Cover",
        },
      ],
      imageHorizontalAlignOptions: [
        {
          text: this.$t("admin.events.settings.branding.horizontalAlignOptions.left"),
          value: "Left",
        },
        {
          text: this.$t("admin.events.settings.branding.horizontalAlignOptions.center"),
          value: "Center",
        },
        {
          text: this.$t("admin.events.settings.branding.horizontalAlignOptions.right"),
          value: "Right",
        },
      ],
      imageVerticalAlignOptions: [
        {
          text: this.$t("admin.events.settings.branding.verticalAlignOptions.top"),
          value: "Top",
        },
        {
          text: this.$t("admin.events.settings.branding.verticalAlignOptions.middle"),
          value: "Center",
        },
        {
          text: this.$t("admin.events.settings.branding.verticalAlignOptions.bottom"),
          value: "Bottom",
        },
      ],
    };
  },
  mounted() {
    this.getEventFromApi();
  },
  methods: {
    async getEventFromApi() {
      this.loading = true;
      this.event = await axios.get(`/admin/events/${this.$route.params.eventId}`);

      this.loading = false;
    },
    async save() {
      this.view = true;
      this.editOn = false;

      if (this.fileLogo) {
        var formData = new FormData();
        formData.append("file", this.fileLogo);
        const axiosResponse = await axios.post("/files", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.event.brandingLogoUrl = axiosResponse.serveURL;
      }

      if (this.fileBanner) {
        var formDataBanner = new FormData();
        formDataBanner.append("file", this.fileBanner);
        const axiosResponse = await axios.post("/files", formDataBanner, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.event.brandingBannerUrl = axiosResponse.serveURL;
      }

      if (this.fileBackground) {
        var formDataBackground = new FormData();
        formDataBackground.append("file", this.fileBackground);
        const axiosResponse = await axios.post("/files", formDataBackground, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.event.brandingContentImageUrl = axiosResponse.serveURL;
      }

      if (this.fileFavicon) {
        var formDataFavicon = new FormData();
        formDataFavicon.append("file", this.fileFavicon);
        const axiosResponse = await axios.post("/files", formDataFavicon, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.event.brandingFaviconUrl = axiosResponse.serveURL;
      }

      await axios.put(`/admin/events/${this.$route.params.eventId}`, this.event);
      this.$store.commit("SUCCESS", this.$t("admin.events.settings.branding.msgSaved"));
    },
    edit() {
      this.view = false;
      this.editOn = true;
    },
    removeLogoImage() {
      this.event.brandingLogoUrl = null;
      this.fileLogo = null;
    },
    removeBannerImage() {
      this.event.brandingBannerUrl = null;
      this.fileBanner = null;
    },
    removeBackgroundImage() {
      this.event.brandingContentImageUrl = null;
      this.fileBackground = null;
    },
  },
};
</script>

<style scoped></style>
